import {
  SBEF_CUSTOM_GROUP_IDS,
  SbefCustomGroupId,
  SbefScopeGroupKey,
  sbefScopeGroupKeys,
} from '@shared/construction/sbef-custom-groups.data';
import { normalizeSbefScopeGroupKey } from '@shared/helpers/sbef-grouping.helpers';

/** SBEF scope group keys the user can toggle (standard digits; not A5.1 / A5.2-5). */
export const SBEF_SELECTABLE_SCOPE_GROUP_KEYS = sbefScopeGroupKeys.filter(
  (key): key is SbefScopeGroupKey =>
    !SBEF_CUSTOM_GROUP_IDS.includes(key as SbefCustomGroupId),
);

const isA5CustomSbefGroup = (key: SbefScopeGroupKey): boolean =>
  SBEF_CUSTOM_GROUP_IDS.includes(key as SbefCustomGroupId);

/**
 * Strip A5 custom group keys and fold legacy General keys into group 9.
 */
export const sanitizeSelectableSbefGroups = (
  selectedGroups: readonly SbefScopeGroupKey[],
): SbefScopeGroupKey[] => {
  const normalized = selectedGroups
    .filter((key) => !isA5CustomSbefGroup(key))
    .map((key) => normalizeSbefScopeGroupKey(key) as SbefScopeGroupKey);
  return [...new Set(normalized)];
};

/**
 * True when the user has not narrowed SBEF scope (empty or every selectable group checked).
 */
export const isSelectableSbefGroupsNoop = (
  selectedGroups: readonly SbefScopeGroupKey[],
): boolean => {
  const selectable = sanitizeSelectableSbefGroups(selectedGroups);
  return (
    selectable.length === 0 ||
    selectable.length === SBEF_SELECTABLE_SCOPE_GROUP_KEYS.length
  );
};

/**
 * Resolve SBEF groups for filtering. A5.1 / A5.2-5 content is folded into group 9; A5 phases
 * are controlled by the Embodied A5 lifecycle toggle instead.
 */
export const getEffectiveSbefGroups = (
  selectedGroups: readonly SbefScopeGroupKey[],
): readonly SbefScopeGroupKey[] => {
  const selectable = sanitizeSelectableSbefGroups(selectedGroups);
  if (isSelectableSbefGroupsNoop(selectedGroups)) {
    return SBEF_SELECTABLE_SCOPE_GROUP_KEYS;
  }
  return selectable;
};

/**
 * True when the effective SBEF scope equals "show everything" (no SBEF breakdown filter).
 */
export const isEffectiveSbefScopeFilterNoop = (
  selectedGroups: readonly SbefScopeGroupKey[],
): boolean => {
  const effective = getEffectiveSbefGroups(selectedGroups);
  return (
    effective.length === 0 ||
    effective.length === SBEF_SELECTABLE_SCOPE_GROUP_KEYS.length
  );
};
